import { useState } from 'react';
import { api, type ApiError } from '../api/client';
import { Alert } from './Field';
import { RichText } from './RichText';

interface ContentOption {
  id: string;
  title: string;
  kind: string;
  status: string;
}

export interface ProposedLesson {
  number: number;
  title: string;
  html: string;
  durationMinutes: number;
  pageFrom?: number;
  /** The generated course contents; rebuilt from the kept lessons on import. */
  isContents?: boolean;
}

export interface PreviewResult {
  document: {
    id: string;
    title: string;
    documentTitle: string | null;
    pages: number | null;
    truncatedAfter: number | null;
  };
  usedContentsPage: boolean;
  missing: string[];
  lessons: ProposedLesson[];
}

/** A proposed lesson plus the two things a reviewer does to it. */
export interface Draft extends ProposedLesson {
  include: boolean;
  open: boolean;
}

/** What a reviewed list is finally sent as. */
export function toLessonPayload(drafts: Draft[]) {
  return drafts
    .filter((draft) => draft.include)
    .map((draft) => ({
      title: draft.title.trim(),
      html: draft.html,
      durationMinutes: draft.durationMinutes,
      isContents: draft.isContents || undefined,
    }));
}

/**
 * The reviewable list of proposed lessons.
 *
 * Shared by the builder's import panel and the new-course wizard, because
 * reviewing a parser's guesses is the same job in both and two copies would
 * drift — one of them gaining the edit box the other lacks.
 *
 * Each row offers exactly what a reviewer needs: keep it or drop it, fix the
 * title in place, and open it to see the body as it will render with the
 * markup underneath, editable.
 */
export function ProposedLessonList({
  drafts,
  onUpdate,
}: {
  drafts: Draft[];
  onUpdate: (number: number, patch: Partial<Draft>) => void;
}) {
  return (
    <ul className="import__list">
      {drafts.map((draft) => (
        <li className="import__row" key={draft.number}>
          <div className="import__line">
            <input
              type="checkbox"
              checked={draft.include}
              aria-label={`Include ${draft.title}`}
              onChange={(event) => onUpdate(draft.number, { include: event.target.checked })}
            />
            <input
              className="field__input import__title"
              value={draft.title}
              maxLength={250}
              aria-label={`Title of lesson ${draft.number}`}
              disabled={!draft.include}
              onChange={(event) => onUpdate(draft.number, { title: event.target.value })}
            />
            <span className="muted import__meta">
              {draft.isContents ? 'Contents · ' : ''}
              {draft.durationMinutes}m
            </span>
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              aria-expanded={draft.open}
              onClick={() => onUpdate(draft.number, { open: !draft.open })}
            >
              {draft.open ? 'Close' : 'View'}
            </button>
          </div>

          {draft.open ? (
            <div className="import__detail">
              {/* Shown as it will appear, then as the markup behind it. The
                  body is sanitised again on save, so editing it here is no
                  more trusted than typing a lesson by hand. */}
              <p className="muted import__label">How it will look</p>
              <div className="import__preview">
                <RichText html={draft.html} />
              </div>
              <p className="muted import__label">Edit the content</p>
              <textarea
                className="field__input import__editor"
                rows={10}
                value={draft.html}
                aria-label={`Content of lesson ${draft.number}`}
                onChange={(event) => onUpdate(draft.number, { html: event.target.value })}
              />
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** The one-line verdict on what a document produced. */
export function ImportSummary({ result }: { result: PreviewResult }) {
  return (
    <div className="import__summary">
      <strong>{result.lessons.length}</strong> lesson{result.lessons.length === 1 ? '' : 's'} found
      {result.document.pages ? ` in ${result.document.pages} pages` : ''}
      {', each opening with its own contents'}
      {result.usedContentsPage ? ', using the document’s own contents page' : ''}.
      {result.missing.length > 0 ? (
        <span className="import__warn">
          {' '}
          {result.missing.length} listed section{result.missing.length === 1 ? '' : 's'} could not be
          found: {result.missing.join(', ')}.
        </span>
      ) : null}
      {result.document.truncatedAfter !== null ? (
        <span className="import__warn">
          {' '}
          Only the first {result.document.truncatedAfter} pages were read.
        </span>
      ) : null}
    </div>
  );
}

/**
 * Build a course out of a document somebody already wrote.
 *
 * Three steps, in one panel, in the order they happen: choose a document,
 * review what came out of it, create it.
 *
 * The middle step is the point. Everything here is a proposal — a parser's
 * reading of a PDF, which is a page description and not a document, so it
 * gets things wrong. Every title and every body is editable before anything
 * is written, and a lesson can simply be unticked. Nothing reaches the
 * course that a person has not looked at.
 */
export function ImportFromDocument({
  courseId,
  documents,
  onImported,
}: {
  courseId: string;
  documents: ContentOption[];
  onImported: () => void;
}) {
  const [chosen, setChosen] = useState('');
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [moduleTitle, setModuleTitle] = useState('');
  const [busy, setBusy] = useState<'preview' | 'submit' | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const chosenCount = drafts.filter((draft) => draft.include).length;

  async function preview() {
    if (!chosen) return;
    setBusy('preview');
    setError(null);
    setDone(null);
    try {
      const preview = await api<PreviewResult>(`/admin/courses/${courseId}/import/preview`, {
        method: 'POST',
        body: JSON.stringify({ contentItemId: chosen }),
      });
      setResult(preview);
      setDrafts(preview.lessons.map((lesson) => ({ ...lesson, include: true, open: false })));
      setModuleTitle(preview.document.documentTitle ?? preview.document.title);
    } catch (caught) {
      setError(caught as ApiError);
      setResult(null);
      setDrafts([]);
    } finally {
      setBusy(null);
    }
  }

  function update(number: number, patch: Partial<Draft>) {
    setDrafts((current) =>
      current.map((draft) => (draft.number === number ? { ...draft, ...patch } : draft)),
    );
  }

  async function submit() {
    setBusy('submit');
    setError(null);
    try {
      const created = await api<{ lessonsCreated: number; contentStatus: string }>(
        `/admin/courses/${courseId}/import`,
        {
          method: 'POST',
          body: JSON.stringify({
            moduleTitle: moduleTitle.trim() || undefined,
            sourceTitle: result?.document.title,
            lessons: toLessonPayload(drafts),
          }),
        },
      );
      setDone(
        `${created.lessonsCreated} lesson${created.lessonsCreated === 1 ? '' : 's'} created` +
          (created.contentStatus === 'PUBLISHED'
            ? ', with their content ready. The only approval left is the course’s own.'
            : '. Their content is in the review queue, for somebody who can approve it.'),
      );
      setResult(null);
      setDrafts([]);
      setChosen('');
      onImported();
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="import">
      <div className="import__head">
        <span className="card__title">Import from a document</span>
        <span className="muted" style={{ fontSize: 12.5 }}>
          Splits a PDF or Word document into one lesson per section, keeping its headings,
          lists, tables, code and links.
        </span>
      </div>

      <div className="card__body">
        {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
        {done ? <Alert kind="success" title={done} /> : null}

        <div className="row">
          <select
            className="field__input"
            aria-label="Document to import"
            value={chosen}
            onChange={(event) => setChosen(event.target.value)}
          >
            <option value="">Choose a PDF or Word document from the library…</option>
            {documents.map((document) => (
              <option key={document.id} value={document.id}>
                {document.title}
              </option>
            ))}
          </select>
          <button
            className="btn btn--sm"
            type="button"
            disabled={!chosen || busy !== null}
            onClick={() => void preview()}
          >
            {busy === 'preview' ? 'Reading…' : 'Preview'}
          </button>
        </div>

        {documents.length === 0 ? (
          <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
            No documents in the library yet. Upload a PDF or Word file under Content Library first.
          </p>
        ) : null}
      </div>

      {result ? (
        <>
          <ImportSummary result={result} />

          <ProposedLessonList drafts={drafts} onUpdate={update} />

          <div className="card__body">
            <div className="field">
              <label className="field__label" htmlFor="import-module">
                Put them in a new module called
              </label>
              <input
                id="import-module"
                className="field__input"
                value={moduleTitle}
                maxLength={250}
                onChange={(event) => setModuleTitle(event.target.value)}
              />
            </div>

            <div className="row" style={{ alignItems: 'center' }}>
              <button
                className="btn btn--sm"
                type="button"
                disabled={busy !== null || chosenCount === 0}
                onClick={() => void submit()}
              >
                {busy === 'submit'
                  ? 'Creating…'
                  : `Submit ${chosenCount} lesson${chosenCount === 1 ? '' : 's'}`}
              </button>
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                disabled={busy !== null}
                onClick={() => {
                  setResult(null);
                  setDrafts([]);
                }}
              >
                Cancel
              </button>
              <span className="muted" style={{ fontSize: 12.5 }}>
                Content goes to the review queue, not straight to learners.
              </span>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
